/**
 * Mount-point presence mirrored from the parent window into the sandbox.
 *
 * The parent owns the real ZenFS mounts. When one appears (e.g. a
 * Firestore-backed store mounted after the user signs in) it transfers a
 * `MessagePort` for the mount's filesystem and posts a `mount-add` message; the
 * sandbox mounts the port and records the descriptor (see `MountService`) so app
 * code can poll it or subscribe to changes via the SDK.
 */
import { MOUNT_ADD, MOUNT_REMOVE, REQUEST_MOUNTS } from '../generated/protocol';

export interface SandboxMount {
  /** Absolute path, as the sandbox app sees it (e.g. `/firestore`). */
  path: string;
  /** Backend kind, e.g. `'firestore'`. Lets apps find a mount by type. */
  type: string;
  /** Optional stable identifier, used to disambiguate and to target removal. */
  id?: string;
  /**
   * Access mode of the granted view: `'rw'` or `'ro'`. A live role downgrade
   * re-announces the same mount (same key) with `mode: 'ro'`; the change must
   * NOT be deduped away (see `mountListsEqual`) so app code is notified and the
   * read-only mount surfaces.
   */
  mode?: 'ro' | 'rw';
  /**
   * When set, this mount is a git-library dependency the runtime registers under
   * `/node_modules/<moduleName>/` so the bundler resolves the bare import from the
   * mounted repo BEFORE the CDN pass (LIBRARY_MOUNTS_SPEC L3). The host tags a
   * git-library `mount-add` with the package name (= the `dependencies` key, e.g.
   * `@scope/lib`); the sandbox reads it in `handleMountAdd` and calls
   * `bundler.registerGitLibraryMount(moduleName, path)`. Absent on ordinary mounts.
   */
  moduleName?: string;
}

/**
 * Why a mount was removed (`mount-remove`.`reason`, auth-mount §"mount-remove" /
 * AM2-4). Surfaced on the removed descriptor so apps can say *why* a filesystem
 * vanished instead of failing mutely. An unknown/absent value is tolerated (older
 * host) and read as `'revoked'` — the most conservative reading.
 */
export type MountRemoveReason =
  | 'revoked' //     a durable grant was revoked (revokeGrant / consent withdrawal)
  | 'unshared' //    the user's membership was removed (or downgraded out of existence)
  | 'signed-out' //  sign-out teardown of every tracked mount
  | 'unmounted' //   the app's own unmountSpace (or region teardown)
  | 'deleted'; //    the space was soft-deleted (FILE_SHARING §6.2)

const MOUNT_REMOVE_REASONS: ReadonlySet<string> = new Set([
  'revoked',
  'unshared',
  'signed-out',
  'unmounted',
  'deleted',
]);

/** Normalize an over-the-wire reason, defaulting unknown/absent → `'revoked'`. */
export const asMountRemoveReason = (value: unknown): MountRemoveReason =>
  typeof value === 'string' && MOUNT_REMOVE_REASONS.has(value) ? (value as MountRemoveReason) : 'revoked';

/** A mount delivered to a change listener as REMOVED: its descriptor + the reason. */
export interface RemovedMount extends SandboxMount {
  reason: MountRemoveReason;
}

/** The payload a `MountService` change carries: the new live set + any descriptors
 *  removed by this change (with their reason). `removed` is `[]` for adds/replaces. */
export interface MountChange {
  mounts: SandboxMount[];
  removed: RemovedMount[];
}

/** Parent → sandbox: a mount appeared. Carries a transferred `MessagePort` for its fs. */
export const MOUNT_ADD_MESSAGE = MOUNT_ADD;
/** Parent → sandbox: a mount was removed (identified by `id`), with a `reason`. */
export const MOUNT_REMOVE_MESSAGE = MOUNT_REMOVE;
/** Sandbox → parent: sent once registered, asking the parent to (re-)announce its mounts. */
export const REQUEST_MOUNTS_MESSAGE = REQUEST_MOUNTS;

/** Stable key for a mount, preferring `id` and falling back to `path`. */
export const mountKey = (mount: Pick<SandboxMount, 'path' | 'id'>): string => mount.id ?? mount.path;

/**
 * True when two mount lists are equivalent (used to suppress no-op change
 * events). The key includes `mode` (SPEC_REVIEW AM-3): a re-announce whose ONLY
 * change is a rw→ro downgrade must fire `onChange`, not be deduped — otherwise
 * the app never learns its writable mount became read-only.
 */
export const mountListsEqual = (a: SandboxMount[], b: SandboxMount[]): boolean => {
  if (a.length !== b.length) {
    return false;
  }
  const key = (m: SandboxMount) => `${mountKey(m)}